/**
 * Chart-specific export utilities
 * Handles the business logic of preparing chart data for export
 */

import { exportCSV } from "./export-csv";
import { exportPDF } from "./export-pdf";
import { transformChartDataForExport, transformTimelineDataForExport } from "./export-transformers";

export type ExportFormat = "csv" | "pdf";

export interface ExportChartDataOptions {
  data: any[];
  chartTitle: string;
  metricType: "volume" | "count" | null;
  includeTotal: boolean;
}

/**
 * Sanitize filename for export
 */
const sanitizeFilename = (name: string): string => {
  return name
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
};

/**
 * Round numbers in export data
 */
const roundExportData = (data: any[]): any[] => {
  return data.map((r) => {
    const copy: Record<string, any> = {};
    Object.keys(r).forEach((k) => {
      const v = (r as any)[k];
      if (typeof v === "number") {
        if (Number.isInteger(v)) {
          copy[k] = v;
        } else {
          copy[k] = parseFloat((Math.round(v * 100) / 100).toFixed(2));
        }
      } else {
        copy[k] = v;
      }
    });
    return copy;
  });
};

/**
 * Filter export data by metric type
 */
const filterByMetric = (data: any[], metricType: "volume" | "count" | null): any[] => {
  if (!metricType) return data;

  return data.map((r) => {
    const copy: Record<string, any> = { ...r };
    if (metricType === "volume") {
      if (copy["Number of Deals"] !== undefined) delete copy["Number of Deals"];
    } else if (metricType === "count") {
      if (copy["Invested capital (CHF Mio)"] !== undefined)
        delete copy["Invested capital (CHF Mio)"];
    }
    return copy;
  });
};

/**
 * Detect if data is timeline format
 */
const isTimelineData = (data: any[]): boolean => {
  if (!data || !data.length) return false;
  const firstItem = data[0];
  return (
    firstItem &&
    "volume" in firstItem &&
    "count" in firstItem &&
    !Object.prototype.hasOwnProperty.call(firstItem, "totalVolume")
  );
};

/**
 * Generate filename for export
 */
const generateFilename = (
  chartTitle: string,
  metricType: "volume" | "count" | null,
  isTimeline: boolean,
  extension: "csv" | "pdf"
): string => {
  const metricLabel =
    metricType === "volume"
      ? "Invested capital"
      : metricType === "count"
        ? "Number of deals"
        : "Data";

  const filename = isTimeline
    ? `${sanitizeFilename(metricLabel)}.${extension}`
    : `${sanitizeFilename(chartTitle)}_${sanitizeFilename(metricLabel)}.${extension}`;

  return filename;
};

/**
 * Prepare data for export
 */
const prepareExportData = (options: ExportChartDataOptions): any[] => {
  const { data, metricType, includeTotal } = options;

  if (!data || !data.length) return [];

  const isTimeline = isTimelineData(data);

  // Transform data based on type
  let exportData: any[];
  if (isTimeline) {
    exportData = transformTimelineDataForExport(data);
  } else {
    exportData = transformChartDataForExport(data, { includeTotal });
  }

  if (!exportData || !exportData.length) {
    exportData = data;
  }

  // Filter by metric if specified
  exportData = filterByMetric(exportData, metricType);

  // Round numbers
  exportData = roundExportData(exportData);

  return exportData;
};

/**
 * Generate PDF HTML table from data
 */
const generatePDFTable = (data: any[]): string => {
  if (!data || !data.length) return "";

  const keys = Object.keys(data[0]);
  const columnWidth = `${100 / keys.length}%`;

  // Generate header
  const header = keys
    .map((k) => {
      if (k === "Invested capital (CHF Mio)") {
        return (
          `<th style="width:${columnWidth};padding:24px 20px;border:none;border-bottom:1px solid #a6a6a6;border-right:8px solid transparent;text-align:left;white-space:nowrap;vertical-align:middle;font-size:22px;">` +
          `<div style="font-weight:700">Invested capital</div>` +
          `<div style="font-size:16px;margin-top:4px;font-weight:400">in CHF Mio.</div>` +
          `</th>`
        );
      }
      if (k === "Number of Deals") {
        return `<th style="width:${columnWidth};padding:24px 20px;border:none;border-bottom:1px solid #a6a6a6;border-right:8px solid transparent;text-align:left;white-space:nowrap;vertical-align:middle;font-size:22px;">Number of deals</th>`;
      }
      return `<th style="width:${columnWidth};padding:24px 20px;border:none;border-bottom:1px solid #a6a6a6;border-right:8px solid transparent;text-align:left;white-space:nowrap;vertical-align:middle;font-size:22px;">${k}</th>`;
    })
    .join("");

  // Generate rows
  const rows = data
    .map((r, index) => {
      const isLastRow = index === data.length - 1;
      const borderBottom = isLastRow ? "none" : "1px solid #a6a6a6";
      return `<tr>${Object.keys(r)
        .map(
          (k) =>
            `<td style="width:${columnWidth};padding:0 18px 20px 12px;border:none;border-bottom:${borderBottom};border-right:8px solid transparent;text-align:left;white-space:nowrap;word-break:normal;font-size:20px;color:#2b2826;vertical-align:middle;line-height:1;">${(r as any)[k] == null ? "" : String((r as any)[k])}</td>`
        )
        .join("")}</tr>`;
    })
    .join("");

  // Origin section
  const originObj = {
    name: "startupticker.ch",
    url: "https://www.startupticker.ch/en/swiss-venture-capital-report",
    description: "",
    logo: "assets/icons/logo.png",
  };

  const originHtml = `
    <div style="display:flex;gap:6%;align-items:flex-start;margin-bottom:36px">
      ${originObj.logo ? `<img src="${originObj.logo}" alt="${originObj.name}" style="width:30%;height:auto;object-fit:contain;display:block;flex-shrink:0">` : ""}
      <div style="width:64%;display:flex;flex-direction:column;justify-content:space-between;min-height:190px">
        <div style="line-height:1.2;margin-top:-12px">
          <div style="font-size:28px;font-weight:700;margin:0;padding:0">Data from the</div>
          <div style="font-size:28px;font-weight:700;margin:0;padding:0">Swiss Venture Capital Report</div>
          <div style="font-size:24px;color:#666;margin-top:8px">by ${originObj.name}</div>
        </div>
        ${originObj.url ? `<div style="font-size:20px;color:#222;line-height:1.8"><div style="margin:0;font-weight:600">See the full report:</div><a href="${originObj.url}" target="_blank" rel="noopener" style="color:#333;text-decoration:underline">startupticker-swiss-venture-capital-report</a></div>` : ""}
      </div>
    </div>
  `;

  const table = `
    <div>
      ${originHtml}
      <table style="width:100%;border-collapse:collapse;border:none;table-layout:fixed;"> 
        <thead><tr>${header}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
  `;

  // Add script to ensure logo sizing
  const script = `
    <script>
      (function(){
        try {
          function setLogoSize(){
            var img = document.querySelector('img[alt="${originObj.name}"]');
            if (img) {
              img.style.width = '30%';
              img.style.height = 'auto';
              img.style.display = 'block';
            }
          }
          if (document.readyState === 'complete') setLogoSize();
          else window.addEventListener('load', setLogoSize);
          setTimeout(setLogoSize, 250);
        } catch(e) { /* ignore */ }
      })();
    </script>
  `;

  return table + script;
};

/**
 * Export chart data in specified format
 */
export const exportChartData = (format: ExportFormat, options: ExportChartDataOptions): void => {
  const { data, chartTitle, metricType } = options;

  if (!data || !data.length) return;

  const isTimeline = isTimelineData(data);
  const exportData = prepareExportData(options);
  const filename = generateFilename(chartTitle, metricType, isTimeline, format);

  if (format === "csv") {
    exportCSV(exportData, filename);
  } else if (format === "pdf") {
    const tableHtml = generatePDFTable(exportData);
    exportPDF(tableHtml, filename);
  }
};
